import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { WidgetEmpty } from '../WidgetCard';
import { Button } from '../../ui/button';
import { announcementsAPI } from '../../../lib/api';
import { ChevronDown, ChevronUp, ExternalLink, Megaphone } from 'lucide-react';
import { formatDate } from '../../../lib/utils';

export const AnnouncementsWidget = ({ data, onRefresh }) => {
  const navigate = useNavigate();
  const [openId, setOpenId] = useState(null);
  const [read, setRead] = useState({});
  const items = data?.items || [];

  const toggle = async (a) => {
    const next = openId === a.id ? null : a.id;
    setOpenId(next);
    if (next && !a.is_read && !read[a.id]) {
      setRead((r) => ({ ...r, [a.id]: true }));
      try { await announcementsAPI.markRead(a.id); } catch { /* non-blocking */ }
    }
  };

  if (!items.length) {
    return <WidgetEmpty testId="announcements-empty">No announcements for you right now.</WidgetEmpty>;
  }

  return (
    <div className="space-y-2" data-testid="announcements-list">
      {items.map((a) => {
        const unread = !a.is_read && !read[a.id];
        const open = openId === a.id;
        return (
          <div key={a.id} className="rounded-md border border-line overflow-hidden">
            <button type="button" onClick={() => toggle(a)}
              className="w-full text-left flex items-start gap-2.5 p-3 hover:bg-surface-sunken
                transition-colors duration-150"
              aria-expanded={open}
              data-testid={`announcement-${a.id}`}>
              {unread && (
                <span className="mt-1.5 w-2 h-2 rounded-full bg-[hsl(var(--brand-accent))] shrink-0"
                  aria-label="Unread" data-testid={`announcement-unread-${a.id}`} />
              )}
              {!unread && <Megaphone className="w-4 h-4 mt-0.5 text-fg-muted shrink-0" />}
              <span className="min-w-0 flex-1">
                <span className={`block text-sm truncate ${unread ? 'font-semibold text-fg' : 'font-medium text-fg'}`}>
                  {a.title}
                </span>
                <span className="block text-xs text-fg-muted mt-0.5">
                  {a.author_name || 'TriCorps360'} · {formatDate(a.publish_at || a.created_at)}
                </span>
              </span>
              {open ? <ChevronUp className="w-4 h-4 text-fg-muted shrink-0" />
                    : <ChevronDown className="w-4 h-4 text-fg-muted shrink-0" />}
            </button>
            {open && (
              <div className="px-3 pb-3 pt-0 border-t border-line-subtle"
                data-testid={`announcement-body-${a.id}`}>
                <div className="prose prose-sm dark:prose-invert max-w-none mt-2 text-fg">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{a.body || ''}</ReactMarkdown>
                </div>
                {a.link_url && (
                  <a href={a.link_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm mt-2 text-[hsl(var(--brand-accent))] hover:underline">
                    {a.link_label || 'Open link'} <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            )}
          </div>
        );
      })}
      <Button variant="ghost" size="sm" className="w-full"
        onClick={() => navigate('/announcements')} data-testid="announcements-view-all">
        View all announcements
      </Button>
    </div>
  );
};

export default AnnouncementsWidget;
